// The homepage ("/" and "/fr"). Content comes from the admin (Website → Homepage)
// through HomeContentProvider; the admin's on-page editor renders this same
// component with the draft (cmsPage) and `preview`.
import React from "react";
import { useLocation } from "react-router-dom";
import SEO from "./SEO";
import SEOContent from "./SEOContent";
import Hero from "../Sections/Hero";
import { HomeContentProvider, useHomeContent } from "./HomeContent";
import { NoEdit } from "./Editable";
import { sectionOrder } from "../data/homeLayout";

// Below-fold homepage sections — lazy-loaded so they don't block first paint
const About = React.lazy(() => import("../Sections/About"));
const CoverageMap = React.lazy(() => import("../Sections/CoverageMap"));
const WhyChooseUs = React.lazy(() => import("../Sections/WhyChooseUs"));
const Services = React.lazy(() => import("../Sections/Services"));
const Team = React.lazy(() => import("../Sections/Team"));
const Testimonial = React.lazy(() => import("../Sections/Testimonial"));
const Blog = React.lazy(() => import("../Sections/Blog"));
// Below-the-fold homepage strips — also lazy to shrink the initial bundle
const HomeCaseStudiesStrip = React.lazy(() => import("./HomeCaseStudiesStrip"));
const HomeFAQ = React.lazy(() => import("./HomeFAQ"));
const HomeFinalCTA = React.lazy(() => import("./HomeFinalCTA"));

// No page-level Organization here: the one full Organization (+ WebSite) definition is
// in public/index.html, generated from src/data/businessInfo.json by
// scripts/sync-org-jsonld.js, and is on every page including this one.

function HomeSections({ preview }) {
  const { c, isFR } = useHomeContent();
  // Shown in the saved order, minus any hidden sections (Website → Homepage → Section order).
  // The hero is always first.
  const byId = {
    about: <About />,
    team: <Team />,
    whyChooseUs: <WhyChooseUs />,
    services: <Services />,
    // The map's text is site-wide content (Website → Navbar, footer & offices)
    coverageMap: <NoEdit><CoverageMap /></NoEdit>,
    testimonial: <Testimonial />,
    blog: <Blog />,
    faq: <HomeFAQ isFR={isFR} />,
    cases: <HomeCaseStudiesStrip isFR={isFR} />,
    seoText: <SEOContent variant="home" isFR={isFR} />,
    cta: <HomeFinalCTA isFR={isFR} />,
  };
  return (
    <>
      {!preview && (
        <SEO
          title={c.seo.metaTitle}
          description={c.seo.metaDescription}
          image={c.seo.ogImage || undefined}
          canonical={isFR ? "/fr" : "/"}
          lang={isFR ? "fr" : "en"}
        />
      )}
      <Hero />
      {sectionOrder(c.layout).map((id) => byId[id] && <React.Fragment key={id}>{byId[id]}</React.Fragment>)}
    </>
  );
}

export default function HomePage({ cmsPage, forceFr, preview = false }) {
  const { pathname } = useLocation();
  const isFR = forceFr === true || (forceFr !== false && pathname.startsWith("/fr"));
  return (
    <HomeContentProvider page={cmsPage} isFR={isFR}>
      <HomeSections preview={preview} />
    </HomeContentProvider>
  );
}
